import { useEffect, useState } from 'react'
import { Box, Stack, Text } from '@mantine/core'
import classes from './EmptyState.module.css'

/**
 * A few lines worth reading when there is nothing to show.
 *
 * An empty panel is the one screen somebody reaches with nothing to do, and
 * "Nothing assigned to you. Enjoy it." spent that moment saying nothing - a
 * grey sentence under a tick, in a box the size of a list. The rest of this
 * application is dense on purpose; this is the one place that can afford a
 * little air.
 */
type Quote = { line: string; who: string }

/**
 * What a quote can be about, so an empty screen shows one that fits it.
 *
 * "Nothing is late" with a line about teamwork under it reads as a random
 * fortune cookie; with Douglas Adams on deadlines it reads as a wink. The
 * topic is what turns the second into the first.
 */
export type QuoteTopic =
  | 'tasks'
  | 'deadlines'
  | 'activity'
  | 'projects'
  | 'teams'
  | 'conversation'
  | 'spending'
  | 'general'

/**
 * The quotes, by topic.
 *
 * **Every one of these is verifiably said by the person named.** A quote
 * misattributed on a work tool is the sort of thing somebody notices,
 * searches, and then trusts the rest of the screen a little less - so the
 * famous-but-dubious ones ("what gets measured gets managed", "a goal
 * without a plan is just a wish") were taken out rather than kept for being
 * well known. Add to this list the same way: a real source, or not at all.
 */
export const QUOTES: Record<Exclude<QuoteTopic, 'general'>, Quote[]> = {
  tasks: [
    { line: 'It is not enough to be busy. The question is: what are we busy about?', who: 'Henry David Thoreau' },
    { line: 'There is nothing so useless as doing efficiently that which should not be done at all.', who: 'Peter Drucker' },
    { line: 'Amateurs sit and wait for inspiration. The rest of us just get up and go to work.', who: 'Stephen King' },
    { line: 'Nothing is particularly hard if you divide it into small jobs.', who: 'Henry Ford' },
    { line: 'Rest is not idleness.', who: 'John Lubbock' },
    { line: 'Done is better than perfect.', who: 'Sheryl Sandberg' },
  ],
  deadlines: [
    { line: 'I love deadlines. I love the whooshing noise they make as they go by.', who: 'Douglas Adams' },
    { line: 'Work expands so as to fill the time available for its completion.', who: 'C. Northcote Parkinson' },
    { line: 'Until we can manage time, we can manage nothing else.', who: 'Peter Drucker' },
    { line: 'You will never find time for anything. If you want time, you must make it.', who: 'Charles Buxton' },
    { line: 'Time you enjoy wasting is not wasted time.', who: 'Marthe Troly-Curtin' },
  ],
  activity: [
    { line: 'Those who cannot remember the past are condemned to repeat it.', who: 'George Santayana' },
    { line: 'Well done is better than well said.', who: 'Benjamin Franklin' },
    { line: 'Rest is not idleness.', who: 'John Lubbock' },
  ],
  projects: [
    { line: 'Simplicity is about subtracting the obvious and adding the meaningful.', who: 'John Maeda' },
    { line: 'Perfection is achieved not when there is nothing more to add, but when there is nothing left to take away.', who: 'Antoine de Saint-Exupéry' },
    { line: 'Innovation is saying no to a thousand things.', who: 'Steve Jobs' },
    { line: 'Nothing is particularly hard if you divide it into small jobs.', who: 'Henry Ford' },
  ],
  teams: [
    { line: 'Alone we can do so little; together we can do so much.', who: 'Helen Keller' },
    { line: 'None of us is as smart as all of us.', who: 'Ken Blanchard' },
  ],
  spending: [
    { line: 'Beware of little expenses; a small leak will sink a great ship.', who: 'Benjamin Franklin' },
    { line: 'Price is what you pay. Value is what you get.', who: 'Warren Buffett' },
    { line: 'It is not the man who has too little, but the man who craves more, that is poor.', who: 'Seneca' },
    {
      line: 'Annual income twenty pounds, annual expenditure nineteen nineteen and six, result happiness.',
      who: 'Mr Micawber, David Copperfield',
    },
  ],
  conversation: [
    { line: 'The single biggest problem in communication is the illusion that it has taken place.', who: 'William H. Whyte' },
    { line: 'Well done is better than well said.', who: 'Benjamin Franklin' },
  ],
}

/** How long each quote stays before the next. Long enough to read twice. */
export const QUOTE_ROTATION_MS = 12_000

function poolFor(topic: QuoteTopic): Quote[] {
  if (topic !== 'general') return QUOTES[topic]

  // Everything, each once: the same line under two topics should not come
  // round twice as often as the rest.
  const seen = new Map<string, Quote>()
  for (const list of Object.values(QUOTES)) for (const quote of list) seen.set(quote.line, quote)
  return [...seen.values()]
}

/**
 * Shown when a list has nothing in it.
 *
 * **The quote turns over every twelve seconds**, on a timer rather than on
 * render. Per render it changed on every keystroke in a filter above it,
 * which read as a glitch; fixed per visit it became furniture. A slow turn
 * is something to glance back at.
 *
 * **It stops while the pointer is over it**, so somebody halfway through a
 * line is not interrupted by the next one - a quote that vanishes as you
 * read it is worse than none. It starts at a random place, so two empty
 * screens side by side do not say the same thing in step.
 *
 * With reduced motion the quotes still change - they are content, not
 * decoration - but without the fade.
 */
export function EmptyState({
  icon,
  title,
  hint,
  quote = true,
  compact = false,
  topic = 'general',
}: {
  icon: React.ReactNode
  title: string
  /** One line under the title, where there is something useful to say. */
  hint?: string
  quote?: boolean
  /**
   * For a dashboard tile rather than a page panel.
   *
   * A tile is a few hundred pixels tall and can be resized smaller still, so
   * the full version's generous padding would push the quote out of sight
   * and leave a disc floating in a box. Compact keeps every part, tighter.
   */
  compact?: boolean
  /** Which quotes fit this screen. */
  topic?: QuoteTopic
}) {
  const pool = poolFor(topic)
  const [index, setIndex] = useState(() => Math.floor(Math.random() * pool.length))
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (!quote || paused || pool.length < 2) return

    const timer = window.setInterval(() => setIndex((current) => (current + 1) % pool.length), QUOTE_ROTATION_MS)

    return () => window.clearInterval(timer)
  }, [quote, paused, pool.length])

  const chosen = pool[index % pool.length]

  return (
    <Box className={classes.root} data-compact={compact || undefined}>
      <Stack align="center" gap={0}>
        {/* The mark sits in a soft disc rather than floating: a 32px glyph
            alone in a wide panel reads as something that failed to load. */}
        <Box className={classes.mark}>{icon}</Box>

        <Text className={classes.title}>{title}</Text>

        {hint && (
          // Balanced, like the quote, so a two-line hint never strands one
          // word on the second line.
          <Text size={compact ? 'xs' : 'sm'} c="dimmed" ta="center" maw={380} style={{ textWrap: 'balance' }}>
            {hint}
          </Text>
        )}

        {quote && chosen && (
          <Box
            className={classes.quote}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            aria-live="polite"
          >
            {/* Keyed on the line, so each new quote mounts and plays the
                fade-in rather than the text swapping in place. */}
            <Box key={chosen.line} className={classes.turn}>
              <Text className={classes.line}>{chosen.line}</Text>
              <Text className={classes.who}>{chosen.who}</Text>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  )
}
